import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { 
  CalendarDays, 
  Users, 
  Building2, 
  DollarSign,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Mail,
  Phone,
  MapPin,
  Clock,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Loader2,
  CalendarRange,
  UtensilsCrossed,
  Monitor,
  Armchair,
  Eye,
  Send,
  ArrowRightLeft,
  X,
  Pencil,
  ClipboardList,
  Utensils,
  Settings2,
  Ban
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';

export default function EventsPage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Inquiries
  const [inquiries, setInquiries] = useState([]);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showInquiryDetailDialog, setShowInquiryDetailDialog] = useState(false);
  const [showProposalDialog, setShowProposalDialog] = useState(false);
  const [inquiryDetailLoading, setInquiryDetailLoading] = useState(false);
  
  // Bookings
  const [bookings, setBookings] = useState([]);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetailDialog, setShowBookingDetailDialog] = useState(false);
  const [bookingDetailLoading, setBookingDetailLoading] = useState(false);
  const [showAssignSpaceDialog, setShowAssignSpaceDialog] = useState(false);
  const [showAddCateringDialog, setShowAddCateringDialog] = useState(false);
  const [showCreateBEODialog, setShowCreateBEODialog] = useState(false);
  
  // Function Spaces
  const [spaces, setSpaces] = useState([]);
  const [showSpaceDialog, setShowSpaceDialog] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [showSpaceDetailDialog, setShowSpaceDetailDialog] = useState(false);
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [spaceDetailLoading, setSpaceDetailLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  
  // Form states
  const [inquiryForm, setInquiryForm] = useState({
    client_name: '',
    company_name: '',
    contact_email: '',
    contact_phone: '',
    event_type: '',
    event_name: '',
    event_date: '',
    expected_attendance: '',
    space_requirements: '',
    catering_required: false,
    av_requirements: '',
    budget_range: '',
    special_requests: '',
    source: 'website',
    priority: 'medium'
  });
  
  const [spaceForm, setSpaceForm] = useState({
    name: '',
    code: '',
    description: '',
    location: '',
    floor: '',
    area_sqm: '',
    height_m: '',
    is_divisible: false,
    setup_time_minutes: 60,
    teardown_time_minutes: 60,
    min_rental_hours: 2
  });

  const [proposalForm, setProposalForm] = useState({
    event_name: '',
    event_date: '',
    valid_until: '',
    space_total: 0,
    catering_total: 0,
    equipment_total: 0,
    other_total: 0,
    discount_amount: 0,
    discount_reason: '',
    tax_amount: 0,
    deposit_required: 0,
    payment_terms: '',
    cancellation_policy: '',
    introduction_text: '',
    terms_conditions: ''
  });

  const [assignSpaceForm, setAssignSpaceForm] = useState({
    space_id: '',
    assignment_date: '',
    start_time: '09:00',
    end_time: '17:00',
    setup_type: '',
    attendees: '',
    setup_instructions: '',
    rental_amount: 0,
    notes: ''
  });

  const [cateringForm, setCateringForm] = useState({
    service_date: '',
    service_time: '12:00',
    meal_type: '',
    location: '',
    guest_count: '',
    setup_style: '',
    special_dietary: '',
    notes: ''
  });

  const [beoForm, setBeoForm] = useState({
    event_date: '',
    client_arrival: '',
    event_start: '',
    event_end: '',
    client_departure: '',
    room_setup_by: '',
    av_check_by: '',
    catering_ready_by: '',
    banquet_captain: '',
    servers_required: '',
    bartenders_required: '',
    av_technician_required: false,
    setup_notes: '',
    catering_notes: '',
    av_notes: '',
    billing_notes: ''
  });

  const [availabilityForm, setAvailabilityForm] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    if (currentProperty?.id) {
      fetchDashboard();
      fetchInquiries();
      fetchBookings();
      fetchSpaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProperty?.id]);

  const fetchDashboard = async () => {
    try {
      const response = await api.get(`/events/dashboard?property_id=${currentProperty.id}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await api.get(`/events/inquiries?property_id=${currentProperty.id}`);
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await api.get(`/events/bookings?property_id=${currentProperty.id}`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpaces = async () => {
    try {
      const response = await api.get(`/events/spaces?property_id=${currentProperty.id}`);
      setSpaces(response.data);
    } catch (error) {
      console.error('Error fetching spaces:', error);
    }
  };

  // ===== INQUIRY ACTIONS =====
  const handleCreateInquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/inquiries?property_id=${currentProperty.id}`, {
        ...inquiryForm,
        expected_attendance: inquiryForm.expected_attendance ? parseInt(inquiryForm.expected_attendance) : null
      });
      toast.success('Event inquiry created successfully');
      setShowInquiryDialog(false);
      setInquiryForm({
        client_name: '', company_name: '', contact_email: '', contact_phone: '',
        event_type: '', event_name: '', event_date: '', expected_attendance: '',
        space_requirements: '', catering_required: false, av_requirements: '',
        budget_range: '', special_requests: '', source: 'website', priority: 'medium'
      });
      fetchInquiries();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create inquiry');
      console.error('Error creating inquiry:', error);
    }
  };

  const handleViewInquiryDetails = async (inquiry) => {
    setInquiryDetailLoading(true);
    setShowInquiryDetailDialog(true);
    try {
      const response = await api.get(`/events/inquiries/${inquiry.id}`);
      setSelectedInquiry(response.data);
    } catch (error) {
      toast.error('Failed to load inquiry details');
      console.error('Error fetching inquiry details:', error);
      setSelectedInquiry(inquiry);
    } finally {
      setInquiryDetailLoading(false);
    }
  };

  const handleCreateProposal = (inquiry) => {
    setSelectedInquiry(inquiry);
    setProposalForm({
      inquiry_id: inquiry.id,
      event_name: inquiry.event_name || `${inquiry.event_type} Event`,
      event_date: inquiry.event_date || '',
      valid_until: '',
      space_total: 0,
      catering_total: 0,
      equipment_total: 0,
      other_total: 0,
      discount_amount: 0,
      discount_reason: '',
      tax_amount: 0,
      deposit_required: 0,
      payment_terms: 'Net 30',
      cancellation_policy: '48 hours notice required for full refund',
      introduction_text: `Dear ${inquiry.client_name},\n\nThank you for your interest in hosting your event at our venue. Please find our proposal below.`,
      terms_conditions: 'Standard terms and conditions apply.'
    });
    setShowProposalDialog(true);
  };

  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...proposalForm,
        space_total: parseFloat(proposalForm.space_total) || 0,
        catering_total: parseFloat(proposalForm.catering_total) || 0,
        equipment_total: parseFloat(proposalForm.equipment_total) || 0,
        other_total: parseFloat(proposalForm.other_total) || 0,
        discount_amount: parseFloat(proposalForm.discount_amount) || 0,
        tax_amount: parseFloat(proposalForm.tax_amount) || 0,
        deposit_required: parseFloat(proposalForm.deposit_required) || 0,
      };
      if (payload.event_date) {
        payload.event_date = payload.event_date;
      } else {
        delete payload.event_date;
      }
      if (payload.valid_until) {
        payload.valid_until = payload.valid_until;
      } else {
        delete payload.valid_until;
      }
      await api.post(`/events/proposals?property_id=${currentProperty.id}`, payload);
      toast.success('Proposal created successfully');
      setShowProposalDialog(false);
      // Update inquiry status to proposal_sent
      if (selectedInquiry?.id) {
        try {
          await api.put(`/events/inquiries/${selectedInquiry.id}`, { status: 'proposal_sent' });
          fetchInquiries();
        } catch (err) {
          console.error('Error updating inquiry status:', err);
        }
      }
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create proposal');
      console.error('Error creating proposal:', error);
    }
  };

  const handleConvertInquiry = async (inquiryId) => {
    try {
      await api.post(`/events/inquiries/${inquiryId}/convert?property_id=${currentProperty.id}`);
      toast.success('Inquiry converted to booking successfully');
      fetchInquiries();
      fetchBookings();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to convert inquiry to booking');
      console.error('Error converting inquiry:', error);
    }
  };

  const handleMarkAsLost = async (inquiryId) => {
    try {
      await api.put(`/events/inquiries/${inquiryId}`, { status: 'lost', lost_reason: 'Marked as lost by admin' });
      toast.success('Inquiry marked as lost');
      fetchInquiries();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update inquiry');
      console.error('Error marking inquiry as lost:', error);
    }
  };

  // ===== SPACE ACTIONS =====
  const handleCreateSpace = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/spaces?property_id=${currentProperty.id}`, {
        ...spaceForm,
        area_sqm: spaceForm.area_sqm ? parseFloat(spaceForm.area_sqm) : null,
        height_m: spaceForm.height_m ? parseFloat(spaceForm.height_m) : null
      });
      toast.success('Function space created successfully');
      setShowSpaceDialog(false);
      setSpaceForm({
        name: '', code: '', description: '', location: '', floor: '',
        area_sqm: '', height_m: '', is_divisible: false,
        setup_time_minutes: 60, teardown_time_minutes: 60, min_rental_hours: 2
      });
      fetchSpaces();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create function space');
      console.error('Error creating space:', error);
    }
  };

  const handleViewSpaceDetails = async (space) => {
    setSpaceDetailLoading(true);
    setShowSpaceDetailDialog(true);
    try {
      const response = await api.get(`/events/spaces/${space.id}`);
      setSelectedSpace(response.data);
    } catch (error) {
      toast.error('Failed to load space details');
      console.error('Error fetching space details:', error);
      setSelectedSpace(space);
    } finally {
      setSpaceDetailLoading(false);
    }
  };

  const handleCheckAvailability = async (space) => {
    setSelectedSpace(space);
    setAvailabilityData(null);
    setShowAvailabilityDialog(true);
  };

  const handleFetchAvailability = async () => {
    if (!selectedSpace?.id) return;
    setAvailabilityLoading(true);
    try {
      const response = await api.get(
        `/events/spaces/${selectedSpace.id}/availability?start_date=${availabilityForm.start_date}&end_date=${availabilityForm.end_date}`
      );
      setAvailabilityData(response.data);
      toast.success('Availability data loaded');
    } catch (error) {
      toast.error('Failed to check availability');
      console.error('Error checking availability:', error);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // ===== BOOKING ACTIONS =====
  const handleViewBookingDetails = async (booking) => {
    setBookingDetailLoading(true);
    setShowBookingDetailDialog(true);
    try {
      const response = await api.get(`/events/bookings/${booking.id}`);
      setSelectedBooking(response.data);
    } catch (error) {
      toast.error('Failed to load booking details');
      console.error('Error fetching booking details:', error);
      setSelectedBooking(booking);
    } finally {
      setBookingDetailLoading(false);
    }
  };

  const handleOpenAssignSpace = (booking) => {
    setSelectedBooking(booking);
    setAssignSpaceForm({
      space_id: '', assignment_date: booking.event_date || '', start_time: '09:00',
      end_time: '17:00', setup_type: '', attendees: booking.expected_attendance || '',
      setup_instructions: '', rental_amount: 0, notes: ''
    });
    setShowAssignSpaceDialog(true);
  };

  const handleSubmitAssignSpace = async (e) => {
    e.preventDefault();
    if (!selectedBooking?.id) return;
    try {
      await api.post(`/events/bookings/${selectedBooking.id}/spaces`, {
        ...assignSpaceForm,
        attendees: assignSpaceForm.attendees ? parseInt(assignSpaceForm.attendees) : null,
        rental_amount: parseFloat(assignSpaceForm.rental_amount) || 0
      });
      toast.success('Space assigned to booking successfully');
      setShowAssignSpaceDialog(false);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to assign space');
      console.error('Error assigning space:', error);
    }
  };

  const handleOpenAddCatering = (booking) => {
    setSelectedBooking(booking);
    setCateringForm({
      service_date: booking.event_date || '', service_time: '12:00', meal_type: '',
      location: '', guest_count: booking.expected_attendance || '', setup_style: '',
      special_dietary: '', notes: ''
    });
    setShowAddCateringDialog(true);
  };

  const handleSubmitCatering = async (e) => {
    e.preventDefault();
    if (!selectedBooking?.id) return;
    try {
      await api.post(`/events/bookings/${selectedBooking.id}/catering`, {
        ...cateringForm,
        guest_count: parseInt(cateringForm.guest_count) || 0
      });
      toast.success('Catering added to booking successfully');
      setShowAddCateringDialog(false);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to add catering');
      console.error('Error adding catering:', error);
    }
  };

  const handleOpenCreateBEO = (booking) => {
    setSelectedBooking(booking);
    setBeoForm({
      event_date: booking.event_date || '', client_arrival: '', event_start: '09:00',
      event_end: '17:00', client_departure: '', room_setup_by: '07:00', av_check_by: '08:00',
      catering_ready_by: '08:30', banquet_captain: '', servers_required: '',
      bartenders_required: '', av_technician_required: false, setup_notes: '',
      catering_notes: '', av_notes: '', billing_notes: ''
    });
    setShowCreateBEODialog(true);
  };

  const handleSubmitBEO = async (e) => {
    e.preventDefault();
    if (!selectedBooking?.id) return;
    try {
      await api.post(`/events/bookings/${selectedBooking.id}/beo`, {
        ...beoForm,
        servers_required: beoForm.servers_required ? parseInt(beoForm.servers_required) : null,
        bartenders_required: beoForm.bartenders_required ? parseInt(beoForm.bartenders_required) : null
      });
      toast.success('Banquet Event Order created successfully');
      setShowCreateBEODialog(false);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to create BEO');
      console.error('Error creating BEO:', error);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await api.put(`/events/bookings/${bookingId}`, { status: 'cancelled' });
      toast.success('Booking cancelled');
      fetchBookings();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to cancel booking');
      console.error('Error cancelling booking:', error);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await api.put(`/events/bookings/${bookingId}`, { status: newStatus });
      toast.success(`Booking status updated to ${newStatus}`);
      fetchBookings();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update booking status');
      console.error('Error updating booking:', error);
    }
  };

  // ===== HELPERS =====
  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'New' },
      contacted: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', label: 'Contacted' },
      qualified: { color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300', label: 'Qualified' },
      proposal_sent: { color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300', label: 'Proposal Sent' },
      negotiating: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300', label: 'Negotiating' },
      won: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', label: 'Won' },
      lost: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', label: 'Lost' },
      tentative: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', label: 'Tentative' },
      confirmed: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Confirmed' },
      definite: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', label: 'Definite' },
      cancelled: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', label: 'Cancelled' },
      completed: { color: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300', label: 'Completed' },
      draft: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300', label: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', label: 'Sent' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { color: 'bg-slate-100 text-slate-700', label: 'Low' },
      medium: { color: 'bg-blue-100 text-blue-700', label: 'Medium' },
      high: { color: 'bg-orange-100 text-orange-700', label: 'High' },
      urgent: { color: 'bg-red-100 text-red-700', label: 'Urgent' }
    };
    const c = config[priority] || config.medium;
    return <Badge variant="outline" className={c.color}>{c.label}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="events-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property from the header to view events</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="events-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="events-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Events & MICE</h1>
          <p className="text-muted-foreground">Manage events, meetings, and banqueting</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showSpaceDialog} onOpenChange={setShowSpaceDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-space-btn">
                <Building2 className="mr-2 h-4 w-4" />
                Add Space
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Function Space</DialogTitle>
                <DialogDescription>Create a new venue for events</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSpace} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="space-name">Space Name *</Label>
                    <Input id="space-name" value={spaceForm.name} onChange={(e) => setSpaceForm({...spaceForm, name: e.target.value})} placeholder="Grand Ballroom" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="space-code">Code</Label>
                    <Input id="space-code" value={spaceForm.code} onChange={(e) => setSpaceForm({...spaceForm, code: e.target.value})} placeholder="GBR" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="space-location">Location</Label>
                    <Input id="space-location" value={spaceForm.location} onChange={(e) => setSpaceForm({...spaceForm, location: e.target.value})} placeholder="Ground Floor, Wing A" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="space-floor">Floor</Label>
                    <Input id="space-floor" value={spaceForm.floor} onChange={(e) => setSpaceForm({...spaceForm, floor: e.target.value})} placeholder="Ground" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="space-area">Area (sqm)</Label>
                    <Input id="space-area" type="number" step="0.01" value={spaceForm.area_sqm} onChange={(e) => setSpaceForm({...spaceForm, area_sqm: e.target.value})} placeholder="500" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="space-height">Height (m)</Label>
                    <Input id="space-height" type="number" step="0.1" value={spaceForm.height_m} onChange={(e) => setSpaceForm({...spaceForm, height_m: e.target.value})} placeholder="4.5" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="space-description">Description</Label>
                  <Textarea id="space-description" value={spaceForm.description} onChange={(e) => setSpaceForm({...spaceForm, description: e.target.value})} placeholder="Elegant ballroom with crystal chandeliers..." rows={3} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowSpaceDialog(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-space-btn">Create Space</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
            <DialogTrigger asChild>
              <Button data-testid="new-inquiry-btn">
                <Plus className="mr-2 h-4 w-4" />
                New Inquiry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Event Inquiry</DialogTitle>
                <DialogDescription>Capture event inquiry details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateInquiry} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client-name">Client Name *</Label>
                    <Input id="client-name" value={inquiryForm.client_name} onChange={(e) => setInquiryForm({...inquiryForm, client_name: e.target.value})} placeholder="John Smith" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Company</Label>
                    <Input id="company-name" value={inquiryForm.company_name} onChange={(e) => setInquiryForm({...inquiryForm, company_name: e.target.value})} placeholder="Acme Corp" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input id="contact-email" type="email" value={inquiryForm.contact_email} onChange={(e) => setInquiryForm({...inquiryForm, contact_email: e.target.value})} placeholder="john@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone">Phone</Label>
                    <Input id="contact-phone" value={inquiryForm.contact_phone} onChange={(e) => setInquiryForm({...inquiryForm, contact_phone: e.target.value})} placeholder="+251 911 123 456" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event-type">Event Type *</Label>
                    <Select value={inquiryForm.event_type} onValueChange={(value) => setInquiryForm({...inquiryForm, event_type: value})}>
                      <SelectTrigger id="event-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wedding">Wedding</SelectItem>
                        <SelectItem value="conference">Conference</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="seminar">Seminar</SelectItem>
                        <SelectItem value="social">Social Event</SelectItem>
                        <SelectItem value="corporate">Corporate Event</SelectItem>
                        <SelectItem value="exhibition">Exhibition</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event-name">Event Name</Label>
                    <Input id="event-name" value={inquiryForm.event_name} onChange={(e) => setInquiryForm({...inquiryForm, event_name: e.target.value})} placeholder="Annual Conference 2025" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event-date">Event Date</Label>
                    <Input id="event-date" type="date" value={inquiryForm.event_date} onChange={(e) => setInquiryForm({...inquiryForm, event_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expected-attendance">Expected Attendance</Label>
                    <Input id="expected-attendance" type="number" value={inquiryForm.expected_attendance} onChange={(e) => setInquiryForm({...inquiryForm, expected_attendance: e.target.value})} placeholder="150" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="source">Source</Label>
                    <Select value={inquiryForm.source} onValueChange={(value) => setInquiryForm({...inquiryForm, source: value})}>
                      <SelectTrigger id="source"><SelectValue placeholder="Select source" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="referral">Referral</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="walk_in">Walk-in</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={inquiryForm.priority} onValueChange={(value) => setInquiryForm({...inquiryForm, priority: value})}>
                      <SelectTrigger id="priority"><SelectValue placeholder="Select priority" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget-range">Budget Range</Label>
                  <Input id="budget-range" value={inquiryForm.budget_range} onChange={(e) => setInquiryForm({...inquiryForm, budget_range: e.target.value})} placeholder="ETB 100,000 - 200,000" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="special-requests">Special Requests</Label>
                  <Textarea id="special-requests" value={inquiryForm.special_requests} onChange={(e) => setInquiryForm({...inquiryForm, special_requests: e.target.value})} placeholder="Any special requirements or notes..." rows={3} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowInquiryDialog(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-inquiry-btn">Create Inquiry</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ============================================================ */}
      {/* INQUIRY DETAIL DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showInquiryDetailDialog} onOpenChange={setShowInquiryDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="inquiry-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Inquiry Details
            </DialogTitle>
            <DialogDescription>
              {selectedInquiry?.inquiry_number || 'Loading...'}
            </DialogDescription>
          </DialogHeader>
          {inquiryDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : selectedInquiry ? (
            <div className="space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(selectedInquiry.status)}
                {getPriorityBadge(selectedInquiry.priority)}
                <span className="text-sm text-muted-foreground capitalize">{selectedInquiry.event_type?.replace('_', ' ')}</span>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Client Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{selectedInquiry.client_name}</span>
                    </div>
                    {selectedInquiry.company_name && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedInquiry.company_name}</span>
                      </div>
                    )}
                    {selectedInquiry.contact_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedInquiry.contact_email}</span>
                      </div>
                    )}
                    {selectedInquiry.contact_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedInquiry.contact_phone}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Event Details</h4>
                  <div className="space-y-2">
                    {selectedInquiry.event_name && (
                      <div><span className="text-muted-foreground text-sm">Event:</span> <span className="font-medium">{selectedInquiry.event_name}</span></div>
                    )}
                    {selectedInquiry.event_date && (
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDate(selectedInquiry.event_date)}</span>
                      </div>
                    )}
                    {selectedInquiry.expected_attendance && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedInquiry.expected_attendance} expected guests</span>
                      </div>
                    )}
                    {selectedInquiry.budget_range && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedInquiry.budget_range}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {(selectedInquiry.space_requirements || selectedInquiry.av_requirements || selectedInquiry.special_requests) && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Requirements</h4>
                    {selectedInquiry.space_requirements && (
                      <div><span className="text-muted-foreground text-sm">Space:</span> <p className="mt-1">{selectedInquiry.space_requirements}</p></div>
                    )}
                    {selectedInquiry.av_requirements && (
                      <div><span className="text-muted-foreground text-sm">AV:</span> <p className="mt-1">{selectedInquiry.av_requirements}</p></div>
                    )}
                    {selectedInquiry.special_requests && (
                      <div><span className="text-muted-foreground text-sm">Special Requests:</span> <p className="mt-1">{selectedInquiry.special_requests}</p></div>
                    )}
                    <div className="flex gap-4">
                      <div><span className="text-muted-foreground text-sm">Catering:</span> <span className="ml-1">{selectedInquiry.catering_required ? 'Yes' : 'No'}</span></div>
                      <div><span className="text-muted-foreground text-sm">Accommodation:</span> <span className="ml-1">{selectedInquiry.accommodation_required ? 'Yes' : 'No'}</span></div>
                    </div>
                  </div>
                </>
              )}
              
              <Separator />
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Tracking</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Source:</span> <span className="ml-1 capitalize">{selectedInquiry.source?.replace('_', ' ') || 'N/A'}</span></div>
                  <div><span className="text-muted-foreground">Created:</span> <span className="ml-1">{formatDate(selectedInquiry.created_at)}</span></div>
                </div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInquiryDetailDialog(false)}>Close</Button>
            {selectedInquiry && selectedInquiry.status !== 'won' && selectedInquiry.status !== 'lost' && (
              <Button onClick={() => { setShowInquiryDetailDialog(false); handleCreateProposal(selectedInquiry); }} data-testid="inquiry-detail-create-proposal-btn">
                <Send className="mr-2 h-4 w-4" />
                Create Proposal
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* CREATE PROPOSAL DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showProposalDialog} onOpenChange={setShowProposalDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-proposal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Create Proposal
            </DialogTitle>
            <DialogDescription>
              Create a proposal for {selectedInquiry?.client_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitProposal} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event Name</Label>
                <Input value={proposalForm.event_name} onChange={(e) => setProposalForm({...proposalForm, event_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Event Date</Label>
                <Input type="date" value={proposalForm.event_date} onChange={(e) => setProposalForm({...proposalForm, event_date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Valid Until</Label>
              <Input type="date" value={proposalForm.valid_until} onChange={(e) => setProposalForm({...proposalForm, valid_until: e.target.value})} />
            </div>
            
            <Separator />
            <h4 className="font-semibold text-sm">Pricing Breakdown</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Space Rental (ETB)</Label>
                <Input type="number" step="0.01" value={proposalForm.space_total} onChange={(e) => setProposalForm({...proposalForm, space_total: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Catering (ETB)</Label>
                <Input type="number" step="0.01" value={proposalForm.catering_total} onChange={(e) => setProposalForm({...proposalForm, catering_total: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Equipment (ETB)</Label>
                <Input type="number" step="0.01" value={proposalForm.equipment_total} onChange={(e) => setProposalForm({...proposalForm, equipment_total: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Other Charges (ETB)</Label>
                <Input type="number" step="0.01" value={proposalForm.other_total} onChange={(e) => setProposalForm({...proposalForm, other_total: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Discount (ETB)</Label>
                <Input type="number" step="0.01" value={proposalForm.discount_amount} onChange={(e) => setProposalForm({...proposalForm, discount_amount: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Tax (ETB)</Label>
                <Input type="number" step="0.01" value={proposalForm.tax_amount} onChange={(e) => setProposalForm({...proposalForm, tax_amount: e.target.value})} />
              </div>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span className="font-medium">{formatCurrency(
                  (parseFloat(proposalForm.space_total) || 0) +
                  (parseFloat(proposalForm.catering_total) || 0) +
                  (parseFloat(proposalForm.equipment_total) || 0) +
                  (parseFloat(proposalForm.other_total) || 0) -
                  (parseFloat(proposalForm.discount_amount) || 0)
                )}</span>
              </div>
              <div className="flex justify-between text-sm font-bold mt-1">
                <span>Grand Total:</span>
                <span>{formatCurrency(
                  (parseFloat(proposalForm.space_total) || 0) +
                  (parseFloat(proposalForm.catering_total) || 0) +
                  (parseFloat(proposalForm.equipment_total) || 0) +
                  (parseFloat(proposalForm.other_total) || 0) -
                  (parseFloat(proposalForm.discount_amount) || 0) +
                  (parseFloat(proposalForm.tax_amount) || 0)
                )}</span>
              </div>
            </div>
            
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deposit Required (ETB)</Label>
                <Input type="number" step="0.01" value={proposalForm.deposit_required} onChange={(e) => setProposalForm({...proposalForm, deposit_required: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Input value={proposalForm.payment_terms} onChange={(e) => setProposalForm({...proposalForm, payment_terms: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Introduction Text</Label>
              <Textarea rows={3} value={proposalForm.introduction_text} onChange={(e) => setProposalForm({...proposalForm, introduction_text: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowProposalDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-proposal-btn">
                <Send className="mr-2 h-4 w-4" />
                Create Proposal
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* SPACE DETAIL DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showSpaceDetailDialog} onOpenChange={setShowSpaceDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="space-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedSpace?.name || 'Space Details'}
            </DialogTitle>
            <DialogDescription>
              {selectedSpace?.code && `Code: ${selectedSpace.code}`}
            </DialogDescription>
          </DialogHeader>
          {spaceDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : selectedSpace ? (
            <div className="space-y-6">
              {selectedSpace.description && (
                <p className="text-muted-foreground">{selectedSpace.description}</p>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Location:</span>
                      <span className="font-medium">{selectedSpace.location || 'N/A'}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Floor:</span>
                      <span className="font-medium">{selectedSpace.floor || 'N/A'}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Area:</span>
                      <span className="ml-1 font-medium">{selectedSpace.area_sqm ? `${selectedSpace.area_sqm} sqm` : 'N/A'}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Height:</span>
                      <span className="ml-1 font-medium">{selectedSpace.height_m ? `${selectedSpace.height_m} m` : 'N/A'}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground">Setup Time:</span>
                  <p className="font-medium">{selectedSpace.setup_time_minutes || 60} min</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground">Teardown Time:</span>
                  <p className="font-medium">{selectedSpace.teardown_time_minutes || 60} min</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground">Min Rental:</span>
                  <p className="font-medium">{selectedSpace.min_rental_hours || 2} hours</p>
                </div>
              </div>

              {selectedSpace.capacities && selectedSpace.capacities.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Seating Capacities</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedSpace.capacities.map((cap) => (
                        <div key={cap.id} className="flex items-center justify-between p-2 border rounded-md">
                          <span className="capitalize text-sm">{cap.setup_type?.replace('_', ' ')}</span>
                          <Badge variant="secondary">{cap.capacity} pax</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {selectedSpace.rates && selectedSpace.rates.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Rental Rates</h4>
                    <div className="space-y-2">
                      {selectedSpace.rates.map((rate) => (
                        <div key={rate.id} className="flex items-center justify-between p-2 border rounded-md">
                          <div>
                            <span className="font-medium text-sm">{rate.rate_name}</span>
                            <span className="ml-2 text-xs text-muted-foreground capitalize">({rate.rate_type})</span>
                          </div>
                          <span className="font-semibold">{formatCurrency(rate.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Divisible:</span>
                <Badge variant={selectedSpace.is_divisible ? "default" : "secondary"}>
                  {selectedSpace.is_divisible ? 'Yes' : 'No'}
                </Badge>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSpaceDetailDialog(false)}>Close</Button>
            <Button onClick={() => { setShowSpaceDetailDialog(false); handleCheckAvailability(selectedSpace); }} data-testid="space-detail-check-availability-btn">
              <CalendarRange className="mr-2 h-4 w-4" />
              Check Availability
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* SPACE AVAILABILITY DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showAvailabilityDialog} onOpenChange={setShowAvailabilityDialog}>
        <DialogContent className="max-w-lg" data-testid="space-availability-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarRange className="h-5 w-5" />
              Check Availability
            </DialogTitle>
            <DialogDescription>
              {selectedSpace?.name || 'Select date range'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={availabilityForm.start_date} onChange={(e) => setAvailabilityForm({...availabilityForm, start_date: e.target.value})} data-testid="availability-start-date" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={availabilityForm.end_date} onChange={(e) => setAvailabilityForm({...availabilityForm, end_date: e.target.value})} data-testid="availability-end-date" />
              </div>
            </div>
            <Button onClick={handleFetchAvailability} disabled={availabilityLoading} className="w-full" data-testid="check-availability-btn">
              {availabilityLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Check Availability
            </Button>

            {availabilityData && (
              <div className="mt-4 space-y-3">
                <Separator />
                <h4 className="font-semibold text-sm">Results for {formatDate(availabilityData.start_date)} - {formatDate(availabilityData.end_date)}</h4>
                {availabilityData.assignments && availabilityData.assignments.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{availabilityData.assignments.length} booking(s) found in this period:</p>
                    {availabilityData.assignments.map((assignment) => (
                      <div key={assignment.id} className="p-3 border rounded-lg text-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{formatDate(assignment.assignment_date)}</span>
                          </div>
                          {getStatusBadge(assignment.status)}
                        </div>
                        <div className="mt-1 text-muted-foreground">
                          <Clock className="inline h-3 w-3 mr-1" />
                          {assignment.start_time} - {assignment.end_time}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center bg-green-50 dark:bg-green-950 rounded-lg">
                    <CheckCircle2 className="h-8 w-8 mx-auto text-green-600 dark:text-green-400 mb-2" />
                    <p className="font-medium text-green-700 dark:text-green-300">Space is available!</p>
                    <p className="text-sm text-green-600 dark:text-green-400">No bookings found in this date range</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvailabilityDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* BOOKING DETAIL DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showBookingDetailDialog} onOpenChange={setShowBookingDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="booking-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Booking Details
            </DialogTitle>
            <DialogDescription>
              {selectedBooking?.booking_number || 'Loading...'}
            </DialogDescription>
          </DialogHeader>
          {bookingDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : selectedBooking ? (
            <div className="space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(selectedBooking.status)}
                <span className="text-sm text-muted-foreground capitalize">{selectedBooking.event_type?.replace('_', ' ')}</span>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Event Information</h4>
                  <div className="space-y-2">
                    <div><span className="text-muted-foreground text-sm">Event:</span> <span className="font-medium ml-1">{selectedBooking.event_name}</span></div>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <span>{formatDate(selectedBooking.event_date)}</span>
                      {selectedBooking.event_end_date && <span> - {formatDate(selectedBooking.event_end_date)}</span>}
                    </div>
                    {selectedBooking.expected_attendance && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedBooking.expected_attendance} expected</span>
                        {selectedBooking.guaranteed_attendance && <span className="text-muted-foreground">({selectedBooking.guaranteed_attendance} guaranteed)</span>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Client</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{selectedBooking.client_name}</span>
                    </div>
                    {selectedBooking.company_name && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedBooking.company_name}</span>
                      </div>
                    )}
                    {selectedBooking.contact_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedBooking.contact_email}</span>
                      </div>
                    )}
                    {selectedBooking.contact_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedBooking.contact_phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Separator />
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Financial</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-muted rounded-lg text-sm">
                    <span className="text-muted-foreground">Total Amount</span>
                    <p className="font-bold text-lg">{formatCurrency(selectedBooking.total_amount)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-sm">
                    <span className="text-muted-foreground">Deposit Required</span>
                    <p className="font-bold text-lg">{formatCurrency(selectedBooking.deposit_required)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-sm">
                    <span className="text-muted-foreground">Deposit Paid</span>
                    <p className="font-bold text-lg">{formatCurrency(selectedBooking.deposit_paid)}</p>
                  </div>
                </div>
              </div>

              {/* Space Assignments */}
              {selectedBooking.space_assignments && selectedBooking.space_assignments.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Assigned Spaces</h4>
                    <div className="space-y-2">
                      {selectedBooking.space_assignments.map((sa) => (
                        <div key={sa.id} className="p-3 border rounded-lg text-sm flex items-center justify-between">
                          <div>
                            <span className="font-medium">{sa.function_spaces?.name || 'Space'}</span>
                            <div className="text-muted-foreground">
                              {formatDate(sa.assignment_date)} | {sa.start_time} - {sa.end_time}
                              {sa.setup_type && <span className="ml-2 capitalize">({sa.setup_type})</span>}
                            </div>
                          </div>
                          {sa.rental_amount > 0 && <span className="font-medium">{formatCurrency(sa.rental_amount)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Catering Orders */}
              {selectedBooking.catering_orders && selectedBooking.catering_orders.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Catering Orders</h4>
                    <div className="space-y-2">
                      {selectedBooking.catering_orders.map((co) => (
                        <div key={co.id} className="p-3 border rounded-lg text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium capitalize">{co.meal_type?.replace('_', ' ')}</span>
                            {getStatusBadge(co.status)}
                          </div>
                          <div className="text-muted-foreground mt-1">
                            {formatDate(co.service_date)} at {co.service_time} | {co.guest_count} guests
                            {co.setup_style && <span className="capitalize"> | {co.setup_style}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* BEOs */}
              {selectedBooking.beos && selectedBooking.beos.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Banquet Event Orders</h4>
                    <div className="space-y-2">
                      {selectedBooking.beos.map((beo) => (
                        <div key={beo.id} className="p-3 border rounded-lg text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium font-mono">{beo.beo_number}</span>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">v{beo.version}</Badge>
                              {getStatusBadge(beo.status)}
                            </div>
                          </div>
                          <div className="text-muted-foreground mt-1">
                            {formatDate(beo.event_date)}
                            {beo.event_start && ` | ${beo.event_start} - ${beo.event_end}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {selectedBooking.special_instructions && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-2">Special Instructions</h4>
                    <p className="text-sm">{selectedBooking.special_instructions}</p>
                  </div>
                </>
              )}
            </div>
          ) : null}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowBookingDetailDialog(false)}>Close</Button>
            {selectedBooking && selectedBooking.status !== 'cancelled' && selectedBooking.status !== 'completed' && (
              <>
                <Button variant="outline" onClick={() => { setShowBookingDetailDialog(false); handleOpenAssignSpace(selectedBooking); }} data-testid="booking-detail-assign-space-btn">
                  <Building2 className="mr-2 h-4 w-4" />
                  Assign Space
                </Button>
                <Button onClick={() => { setShowBookingDetailDialog(false); handleOpenCreateBEO(selectedBooking); }} data-testid="booking-detail-create-beo-btn">
                  <ClipboardList className="mr-2 h-4 w-4" />
                  Create BEO
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* ASSIGN SPACE DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showAssignSpaceDialog} onOpenChange={setShowAssignSpaceDialog}>
        <DialogContent className="max-w-lg" data-testid="assign-space-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Assign Space to Booking
            </DialogTitle>
            <DialogDescription>
              {selectedBooking?.event_name} - {selectedBooking?.booking_number}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitAssignSpace} className="space-y-4">
            <div className="space-y-2">
              <Label>Function Space *</Label>
              <Select value={assignSpaceForm.space_id} onValueChange={(value) => setAssignSpaceForm({...assignSpaceForm, space_id: value})}>
                <SelectTrigger data-testid="assign-space-select"><SelectValue placeholder="Select a space" /></SelectTrigger>
                <SelectContent>
                  {spaces.map((space) => (
                    <SelectItem key={space.id} value={space.id}>{space.name} {space.code ? `(${space.code})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={assignSpaceForm.assignment_date} onChange={(e) => setAssignSpaceForm({...assignSpaceForm, assignment_date: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input type="time" value={assignSpaceForm.start_time} onChange={(e) => setAssignSpaceForm({...assignSpaceForm, start_time: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input type="time" value={assignSpaceForm.end_time} onChange={(e) => setAssignSpaceForm({...assignSpaceForm, end_time: e.target.value})} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Setup Type</Label>
                <Select value={assignSpaceForm.setup_type} onValueChange={(value) => setAssignSpaceForm({...assignSpaceForm, setup_type: value})}>
                  <SelectTrigger><SelectValue placeholder="Select setup" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="theater">Theater</SelectItem>
                    <SelectItem value="classroom">Classroom</SelectItem>
                    <SelectItem value="u_shape">U-Shape</SelectItem>
                    <SelectItem value="boardroom">Boardroom</SelectItem>
                    <SelectItem value="banquet">Banquet</SelectItem>
                    <SelectItem value="cocktail">Cocktail</SelectItem>
                    <SelectItem value="hollow_square">Hollow Square</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Attendees</Label>
                <Input type="number" value={assignSpaceForm.attendees} onChange={(e) => setAssignSpaceForm({...assignSpaceForm, attendees: e.target.value})} placeholder="100" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rental Amount (ETB)</Label>
              <Input type="number" step="0.01" value={assignSpaceForm.rental_amount} onChange={(e) => setAssignSpaceForm({...assignSpaceForm, rental_amount: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Setup Instructions</Label>
              <Textarea value={assignSpaceForm.setup_instructions} onChange={(e) => setAssignSpaceForm({...assignSpaceForm, setup_instructions: e.target.value})} rows={2} placeholder="Stage placement, chair arrangement..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignSpaceDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={!assignSpaceForm.space_id} data-testid="submit-assign-space-btn">Assign Space</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* ADD CATERING DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showAddCateringDialog} onOpenChange={setShowAddCateringDialog}>
        <DialogContent className="max-w-lg" data-testid="add-catering-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Utensils className="h-5 w-5" />
              Add Catering Order
            </DialogTitle>
            <DialogDescription>
              {selectedBooking?.event_name} - {selectedBooking?.booking_number}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCatering} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Service Date *</Label>
                <Input type="date" value={cateringForm.service_date} onChange={(e) => setCateringForm({...cateringForm, service_date: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Service Time *</Label>
                <Input type="time" value={cateringForm.service_time} onChange={(e) => setCateringForm({...cateringForm, service_time: e.target.value})} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meal Type *</Label>
                <Select value={cateringForm.meal_type} onValueChange={(value) => setCateringForm({...cateringForm, meal_type: value})}>
                  <SelectTrigger><SelectValue placeholder="Select meal type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakfast">Breakfast</SelectItem>
                    <SelectItem value="coffee_break">Coffee Break</SelectItem>
                    <SelectItem value="lunch">Lunch</SelectItem>
                    <SelectItem value="dinner">Dinner</SelectItem>
                    <SelectItem value="reception">Reception</SelectItem>
                    <SelectItem value="cocktail">Cocktail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Guest Count *</Label>
                <Input type="number" value={cateringForm.guest_count} onChange={(e) => setCateringForm({...cateringForm, guest_count: e.target.value})} placeholder="100" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Setup Style</Label>
                <Select value={cateringForm.setup_style} onValueChange={(value) => setCateringForm({...cateringForm, setup_style: value})}>
                  <SelectTrigger><SelectValue placeholder="Select style" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buffet">Buffet</SelectItem>
                    <SelectItem value="plated">Plated Service</SelectItem>
                    <SelectItem value="cocktail">Cocktail Style</SelectItem>
                    <SelectItem value="family_style">Family Style</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={cateringForm.location} onChange={(e) => setCateringForm({...cateringForm, location: e.target.value})} placeholder="Main Ballroom" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Special Dietary Requirements</Label>
              <Input value={cateringForm.special_dietary} onChange={(e) => setCateringForm({...cateringForm, special_dietary: e.target.value})} placeholder="10 vegetarian, 5 halal..." />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={cateringForm.notes} onChange={(e) => setCateringForm({...cateringForm, notes: e.target.value})} rows={2} placeholder="Special requirements..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddCateringDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={!cateringForm.meal_type} data-testid="submit-catering-btn">Add Catering</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* CREATE BEO DIALOG */}
      {/* ============================================================ */}
      <Dialog open={showCreateBEODialog} onOpenChange={setShowCreateBEODialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-beo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Create Banquet Event Order
            </DialogTitle>
            <DialogDescription>
              {selectedBooking?.event_name} - {selectedBooking?.booking_number}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitBEO} className="space-y-4">
            <div className="space-y-2">
              <Label>Event Date *</Label>
              <Input type="date" value={beoForm.event_date} onChange={(e) => setBeoForm({...beoForm, event_date: e.target.value})} required />
            </div>
            
            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Event Timeline</h4>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label>Client Arrival</Label>
                <Input type="time" value={beoForm.client_arrival} onChange={(e) => setBeoForm({...beoForm, client_arrival: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Event Start</Label>
                <Input type="time" value={beoForm.event_start} onChange={(e) => setBeoForm({...beoForm, event_start: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Event End</Label>
                <Input type="time" value={beoForm.event_end} onChange={(e) => setBeoForm({...beoForm, event_end: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Client Departure</Label>
                <Input type="time" value={beoForm.client_departure} onChange={(e) => setBeoForm({...beoForm, client_departure: e.target.value})} />
              </div>
            </div>

            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Setup Requirements</h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Room Setup By</Label>
                <Input type="time" value={beoForm.room_setup_by} onChange={(e) => setBeoForm({...beoForm, room_setup_by: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>AV Check By</Label>
                <Input type="time" value={beoForm.av_check_by} onChange={(e) => setBeoForm({...beoForm, av_check_by: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Catering Ready By</Label>
                <Input type="time" value={beoForm.catering_ready_by} onChange={(e) => setBeoForm({...beoForm, catering_ready_by: e.target.value})} />
              </div>
            </div>

            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Staffing</h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Banquet Captain</Label>
                <Input value={beoForm.banquet_captain} onChange={(e) => setBeoForm({...beoForm, banquet_captain: e.target.value})} placeholder="Name" />
              </div>
              <div className="space-y-2">
                <Label>Servers Required</Label>
                <Input type="number" value={beoForm.servers_required} onChange={(e) => setBeoForm({...beoForm, servers_required: e.target.value})} placeholder="8" />
              </div>
              <div className="space-y-2">
                <Label>Bartenders Required</Label>
                <Input type="number" value={beoForm.bartenders_required} onChange={(e) => setBeoForm({...beoForm, bartenders_required: e.target.value})} placeholder="2" />
              </div>
            </div>

            <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Department Notes</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Setup Notes</Label>
                <Textarea value={beoForm.setup_notes} onChange={(e) => setBeoForm({...beoForm, setup_notes: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Catering Notes</Label>
                <Textarea value={beoForm.catering_notes} onChange={(e) => setBeoForm({...beoForm, catering_notes: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>AV Notes</Label>
                <Textarea value={beoForm.av_notes} onChange={(e) => setBeoForm({...beoForm, av_notes: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Billing Notes</Label>
                <Textarea value={beoForm.billing_notes} onChange={(e) => setBeoForm({...beoForm, billing_notes: e.target.value})} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateBEODialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-beo-btn">
                <ClipboardList className="mr-2 h-4 w-4" />
                Create BEO
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* TABS CONTENT */}
      {/* ============================================================ */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="spaces" data-testid="tab-spaces">Function Spaces</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-inquiries">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Inquiries</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.inquiries?.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboard?.inquiries?.new || 0} new, {dashboard?.inquiries?.qualified || 0} qualified
                </p>
              </CardContent>
            </Card>
            <Card data-testid="stat-bookings">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Bookings</CardTitle>
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.bookings?.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboard?.bookings?.confirmed || 0} confirmed, {dashboard?.bookings?.tentative || 0} tentative
                </p>
              </CardContent>
            </Card>
            <Card data-testid="stat-upcoming">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Upcoming Events</CardTitle>
                <CalendarRange className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.bookings?.upcoming || 0}</div>
                <p className="text-xs text-muted-foreground">In the next 30 days</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-revenue">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(dashboard?.bookings?.total_revenue)}</div>
                <p className="text-xs text-muted-foreground">From confirmed bookings</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Upcoming Events</CardTitle>
              <CardDescription>Events scheduled in the coming days</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.upcoming_events?.length > 0 ? (
                <div className="space-y-4">
                  {dashboard.upcoming_events.map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <CalendarDays className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{event.event_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CalendarDays className="h-3 w-3" />
                            <span>{new Date(event.event_date).toLocaleDateString()}</span>
                            <span className="mx-1">|</span>
                            <Users className="h-3 w-3" />
                            <span>{event.expected_attendance || 'TBD'} guests</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(event.status)}
                        <Button variant="ghost" size="icon" onClick={() => handleViewBookingDetails(event)} data-testid={`dashboard-event-view-${event.id}`}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No upcoming events</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Function Spaces</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboard?.function_spaces_count || 0}</div>
                    <p className="text-xs text-muted-foreground">Active venues</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Inquiry Pipeline</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{dashboard?.inquiries?.proposal_sent || 0}</div>
                    <p className="text-xs text-muted-foreground">Proposals pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Conversion Rate</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {dashboard?.inquiries?.total > 0 
                        ? Math.round((dashboard?.bookings?.total / dashboard?.inquiries?.total) * 100) 
                        : 0}%
                    </div>
                    <p className="text-xs text-muted-foreground">Inquiries to bookings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================================ */}
        {/* INQUIRIES TAB */}
        {/* ============================================================ */}
        <TabsContent value="inquiries" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Event Inquiries</CardTitle>
              <CardDescription>Manage and track event inquiries</CardDescription>
            </CardHeader>
            <CardContent>
              {inquiries.length > 0 ? (
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div key={inquiry.id} className="p-4 border rounded-lg" data-testid={`inquiry-${inquiry.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{inquiry.client_name}</span>
                            {inquiry.company_name && (
                              <span className="text-muted-foreground">| {inquiry.company_name}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="capitalize">{inquiry.event_type?.replace('_', ' ')}</span>
                            {inquiry.event_name && <span>| {inquiry.event_name}</span>}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {inquiry.event_date && (
                              <span className="flex items-center gap-1">
                                <CalendarDays className="h-3 w-3" />
                                {new Date(inquiry.event_date).toLocaleDateString()}
                              </span>
                            )}
                            {inquiry.expected_attendance && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {inquiry.expected_attendance} guests
                              </span>
                            )}
                            {inquiry.contact_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {inquiry.contact_email}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getPriorityBadge(inquiry.priority)}
                          {getStatusBadge(inquiry.status)}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`inquiry-actions-${inquiry.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleViewInquiryDetails(inquiry)} data-testid={`inquiry-view-details-${inquiry.id}`}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              {inquiry.status !== 'won' && inquiry.status !== 'lost' && (
                                <DropdownMenuItem onClick={() => handleCreateProposal(inquiry)} data-testid={`inquiry-create-proposal-${inquiry.id}`}>
                                  <Send className="mr-2 h-4 w-4" />
                                  Create Proposal
                                </DropdownMenuItem>
                              )}
                              {inquiry.status !== 'won' && inquiry.status !== 'lost' && (
                                <DropdownMenuItem onClick={() => handleConvertInquiry(inquiry.id)} data-testid={`inquiry-convert-${inquiry.id}`}>
                                  <ArrowRightLeft className="mr-2 h-4 w-4" />
                                  Convert to Booking
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {inquiry.status !== 'lost' && (
                                <DropdownMenuItem className="text-destructive" onClick={() => handleMarkAsLost(inquiry.id)} data-testid={`inquiry-mark-lost-${inquiry.id}`}>
                                  <XCircle className="mr-2 h-4 w-4" />
                                  Mark as Lost
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No inquiries yet</p>
                  <Button variant="link" onClick={() => setShowInquiryDialog(true)} className="mt-2">
                    Create your first inquiry
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================================ */}
        {/* BOOKINGS TAB */}
        {/* ============================================================ */}
        <TabsContent value="bookings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Event Bookings</CardTitle>
              <CardDescription>Manage confirmed event bookings</CardDescription>
            </CardHeader>
            <CardContent>
              {bookings.length > 0 ? (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="p-4 border rounded-lg" data-testid={`booking-${booking.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-muted-foreground">{booking.booking_number}</span>
                            <span className="font-medium">{booking.event_name}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{booking.client_name}</span>
                            {booking.company_name && <span>| {booking.company_name}</span>}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              {new Date(booking.event_date).toLocaleDateString()}
                            </span>
                            {booking.expected_attendance && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {booking.expected_attendance} guests
                              </span>
                            )}
                            {booking.total_amount > 0 && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                {formatCurrency(booking.total_amount)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(booking.status)}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`booking-actions-${booking.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleViewBookingDetails(booking)} data-testid={`booking-view-details-${booking.id}`}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleOpenAssignSpace(booking)} data-testid={`booking-assign-space-${booking.id}`}>
                                    <Building2 className="mr-2 h-4 w-4" />
                                    Assign Spaces
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenAddCatering(booking)} data-testid={`booking-add-catering-${booking.id}`}>
                                    <Utensils className="mr-2 h-4 w-4" />
                                    Add Catering
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenCreateBEO(booking)} data-testid={`booking-create-beo-${booking.id}`}>
                                    <ClipboardList className="mr-2 h-4 w-4" />
                                    Create BEO
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {booking.status === 'tentative' && (
                                    <DropdownMenuItem onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')} data-testid={`booking-confirm-${booking.id}`}>
                                      <CheckCircle2 className="mr-2 h-4 w-4" />
                                      Confirm Booking
                                    </DropdownMenuItem>
                                  )}
                                  {booking.status === 'confirmed' && (
                                    <DropdownMenuItem onClick={() => handleUpdateBookingStatus(booking.id, 'definite')} data-testid={`booking-make-definite-${booking.id}`}>
                                      <CheckCircle2 className="mr-2 h-4 w-4" />
                                      Mark Definite
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-destructive" onClick={() => handleCancelBooking(booking.id)} data-testid={`booking-cancel-${booking.id}`}>
                                    <Ban className="mr-2 h-4 w-4" />
                                    Cancel Booking
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No bookings yet</p>
                  <p className="text-sm mt-1">Convert inquiries to create bookings</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================================ */}
        {/* FUNCTION SPACES TAB */}
        {/* ============================================================ */}
        <TabsContent value="spaces" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Function Spaces</CardTitle>
              <CardDescription>Manage event venues and meeting rooms</CardDescription>
            </CardHeader>
            <CardContent>
              {spaces.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {spaces.map((space) => (
                    <Card key={space.id} className="overflow-hidden" data-testid={`space-${space.id}`}>
                      <div className="h-32 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                        <Building2 className="h-12 w-12 text-primary/50" />
                      </div>
                      <CardContent className="pt-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold">{space.name}</h3>
                            {space.code && (
                              <Badge variant="outline">{space.code}</Badge>
                            )}
                          </div>
                          {space.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {space.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {space.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {space.location}
                              </span>
                            )}
                            {space.area_sqm && (
                              <span>{space.area_sqm} sqm</span>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => handleViewSpaceDetails(space)} data-testid={`space-view-details-${space.id}`}>
                              <Eye className="mr-1.5 h-3.5 w-3.5" />
                              View Details
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleCheckAvailability(space)} data-testid={`space-check-availability-${space.id}`}>
                              <CalendarRange className="mr-1.5 h-3.5 w-3.5" />
                              Availability
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No function spaces configured</p>
                  <Button variant="link" onClick={() => setShowSpaceDialog(true)} className="mt-2">
                    Add your first space
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
